'use client'

import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { NetworkDisplay } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { invalidateVestingBalancesQuery } from '~/components/smart-accounts/vesting/hooks/useVestingBalances'
import { getChain, SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { useRequiredActiveOrg, useTokenVisibilityUpdates } from '~/hooks/org'
import { useNavigation } from '~/hooks/useNavigation'
import { useServerRequests } from '~/hooks/useRequests'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { AssetType, BackendToken, IAddress, NFTBlockType } from '~/types'
import { TokenBalance } from '~/utils/account'
import { getExplorerAddressLinkChainId } from '~/utils/addresses'

export function HideTokenDialogContent({
  smartAccountAddress,
  token,
  chainId,
  setIsOpen,
  assetType,
  nftSymbol,
  nftTokenId,
}: {
  // Absent for watched-address (card) transfers, which have no smart account
  smartAccountAddress?: IAddress
  token: IAddress
  chainId: SupportedChainId
  setIsOpen: (isOpen: boolean) => void
  assetType: AssetType
  nftSymbol?: string
  nftTokenId?: string
}) {
  const { serverPostRequest, isProcessing: isProcessingHideToken } =
    useServerRequests()
  const queryClient = useQueryClient()
  const tokenMetadata = useTokenMetadata({
    address: token,
    chainId,
    tokenId: nftTokenId,
  })
  const org = useRequiredActiveOrg()
  const { hideToken: hideTokenInOrg, hideNFT: hideNFTInOrg } =
    useTokenVisibilityUpdates()

  const navigate = useNavigation()

  const symbol =
    assetType === 'ERC20'
      ? tokenMetadata?.symbol
      : [nftSymbol ?? tokenMetadata?.symbol ?? 'NFT', `#${nftTokenId}`].join(
          ' ',
        )

  const hideToken = useCallback(async () => {
    if (!tokenMetadata) {
      return
    }
    const tokenParams = {
      orgId: org.id,
      address: token,
      chainId,
    }

    const result = await serverPostRequest<BackendToken>({
      path: '/api/org/token/visibility/hide-token',
      params: tokenParams,
      successMessage: `${tokenMetadata.symbol} added to spam list`,
      successMessageOptions: {
        action: {
          label: 'Settings',
          onClick() {
            navigate('/settings/team/tokens')
          },
        },
      },
      errorMessage: 'Failed to report spam',
    })

    if (result) {
      // Invalidate transactions query so it filters out any inbound transactions of the token.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.TRANSACTIONS],
      })
      // Invalidate balances query so it reloads the balances.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORG_BALANCES, org.id],
      })
      // Invalidate accounting items query so it reloads the items.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORG_ACCOUNTING_ITEMS, org.id],
      })
      // Invalidate card transfers and balances so watched-address views drop the token.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CARD_TRANSFERS, org.id],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CARD_BALANCES, org.id],
      })
      // Remove the token from the smart account balances query.
      // Update all queries for this smart account that might contain this token
      // but only where showSpamTokens is false (we don't want to update queries showing spam)
      if (smartAccountAddress) {
        queryClient.setQueriesData(
          {
            queryKey: [QUERY_KEYS.SMART_ACCOUNT_BALANCES, smartAccountAddress],
            predicate: (query) => {
              const options = query.queryKey[2] as
                | {
                    showSpamTokens?: boolean
                    chainId?: number
                  }
                | undefined
              return options?.showSpamTokens !== true
            },
          },
          (oldData?: { balances: TokenBalance[]; totalValue: number }) => {
            if (!oldData) {
              return oldData
            }
            // Filter out the token matching both address and chainId
            const tokenToRemove = oldData.balances.find(
              (balance) =>
                balance.address.toLowerCase() === token.toLowerCase() &&
                balance.chainId === chainId,
            )
            if (!tokenToRemove) {
              return oldData
            }
            return {
              balances: oldData.balances.filter(
                (balance) =>
                  !(
                    balance.address.toLowerCase() === token.toLowerCase() &&
                    balance.chainId === chainId
                  ),
              ),
              totalValue: oldData.totalValue - (tokenToRemove.usdValue ?? 0),
            }
          },
        )
      }
      // Invalidate vesting balances query so it reloads the balances.
      invalidateVestingBalancesQuery(queryClient, org.id)

      hideTokenInOrg(tokenParams)
      setIsOpen(false)
    }
  }, [
    tokenMetadata,
    org.id,
    smartAccountAddress,
    token,
    chainId,
    serverPostRequest,
    navigate,
    queryClient,
    hideTokenInOrg,
    setIsOpen,
  ])

  const hideNFT = useCallback(async () => {
    const params = {
      orgId: org.id,
      chainId,
      blockType: 'TokenId' as NFTBlockType,
      address: token,
      tokenId: nftTokenId!,
      nftType: assetType,
    }

    const response = await serverPostRequest({
      path: '/api/org/token/visibility/hide-nft',
      params,
      successMessage: `${symbol} reported as spam`,
      successMessageOptions: {
        action: {
          label: 'Settings',
          onClick() {
            navigate('/settings/team/tokens/')
          },
        },
      },
      errorMessage: 'Failed to report spam',
    })

    if (response) {
      // Invalidate transactions query so it filters out any inbound transactions of the token.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.TRANSACTIONS],
      })
      // Invalidate card transfers so watched-address feeds drop the token's rows.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CARD_TRANSFERS, org.id],
      })
      hideNFTInOrg(params)
      setIsOpen(false)
    }
  }, [
    org.id,
    chainId,
    token,
    nftTokenId,
    assetType,
    serverPostRequest,
    symbol,
    navigate,
    queryClient,
    hideNFTInOrg,
    setIsOpen,
  ])

  return (
    <SplitsDialogContent
      id={'hideToken'}
      title={`Report spam?`}
      variant="confirmation"
      isLoading={isProcessingHideToken}
      confirmButton={{
        onClick: () => {
          if (assetType === 'ERC20') {
            hideToken()
          } else {
            hideNFT()
          }
        },
        text: 'Confirm',
        eventProperties: {
          token,
          chainId,
        },
      }}
      cancelButton={{
        eventProperties: {
          token,
          chainId,
        },
      }}
    >
      <p>
        Reporting {symbol} on{' '}
        <NetworkDisplay layout="inline-text" chainId={chainId} /> as spam will
        prevent it from being displayed for all members in the {org?.name}{' '}
        workspace.
      </p>
      <p>
        <a
          href={getExplorerAddressLinkChainId(token, chainId)}
          target="_blank"
          rel="noreferrer"
          className="underline"
        >
          View on {getChain(chainId)?.explorer?.name ?? 'block explorer'}
        </a>
      </p>
    </SplitsDialogContent>
  )
}
